import { el, styled } from './svg';

const FRAME = 'stroke:var(--frame);fill:none;stroke-linecap:round;stroke-linejoin:round;';
const DOT = 'fill:var(--dot);';

/** Structure fills — the dark flat tones a night silhouette is built from. */
const BODY = '#1c2637';
const BODY_HI = '#232f44';
const FOOTING = '#3e4a60';

/** How many drawn variants each terrain tile has — maps drift, they don't tile. */
export const TERRAIN_VARIANTS = 3;

/** Sprite ids referenced by `<use href="#…">`. Terrain ids take a variant suffix. */
export const SPRITE = {
  standard: '#pylonShape',
  junction: '#junctionShape',
  crossing: '#crossingShape',
  plant: '#plantShape',
  house: '#houseShape',
  houseGlow: '#houseGlowShape',
  mtn: '#mtnShape',
  water: '#waterShape',
} as const;

export interface Tower500Options {
  /** Horizontal scale against the base junction size. */
  sx: number;
  /** Vertical scale against the base junction size. */
  sy: number;
  /** Board-unit y of the ground line. */
  ground: number;
  steel: string;
  dot: string;
  dotR: number;
  /** Hazard-band the legs — the river-crossing giant. */
  bands?: boolean;
  /** Blinking aircraft-warning beacons on the shield-wire tips. */
  beacon?: boolean;
  /** Beacons hold steady when motion is reduced. */
  animate?: boolean;
}

/* ---------------------------------------------------------------------------
   The 500 kV cat-face tower — the heavy end of the tower family, worn by
   junctions and river crossings (STYLEGUIDE.md §5).

   Vertical stations in base units, ground at y = 34. Horizontal stations are
   half-widths from the centre line. Everything below is expressed against
   these, so the silhouette can be retuned without hunting through paths.

   The shape is a real cat-face pylon: pointed shield-wire ears, a shield-like
   head with a wishbone carrying the centre phase, deep triangular crossarms
   trussed rather than solid, a pinched waist beneath them, and a body that
   splits into two splayed legs.

   The head is left deliberately empty — outline, ears and wishbone, nothing
   else. It is the tower's one open shape, and lattice inside it closes up into
   a grey mass at the size these are actually drawn.
   --------------------------------------------------------------------------- */
const EAR_TIP_Y = -48;
const HEAD_TOP_Y = -42;
const DOT_C_Y = -31;
const HEAD_WIDE_Y = -24;
const HEAD_FOOT_Y = -14;
const ARM_TIP_Y = -13;
const WAIST_Y = -6;
const DOT_OUT_Y = -3;
const SPLIT_Y = 14;
const GROUND_Y = 34;

const EAR_TIP_X = 12.5;
const EAR_ROOT_X = 7;
const HEAD_TOP_X = 13.7;
const HEAD_WIDE_X = 16;
const HEAD_FOOT_X = 10;
const ARM_TIP_X = 30;
const DOT_OUT_X = 18;
const WAIST_X = 5;
const SPLIT_X = 7.5;
const FOOT_X = 15;

/** Half-width of the body between the waist and the leg split. */
const bodyX = (y: number) => WAIST_X + ((SPLIT_X - WAIST_X) * (y - WAIST_Y)) / (SPLIT_Y - WAIST_Y);

/** Half-width of a leg between the split and the ground. */
const legX = (y: number) => SPLIT_X + ((FOOT_X - SPLIT_X) * (y - SPLIT_Y)) / (GROUND_Y - SPLIT_Y);

/** A point on the arm's top chord, `t` from root to tip. */
const armTop = (t: number): [number, number] => [
  HEAD_FOOT_X + (ARM_TIP_X - HEAD_FOOT_X) * t,
  HEAD_FOOT_Y + (ARM_TIP_Y - HEAD_FOOT_Y) * t,
];

/** A point on the arm's lower chord, `u` from the waist out to the tip. */
const armLow = (u: number): [number, number] => [
  WAIST_X + (ARM_TIP_X - WAIST_X) * u,
  WAIST_Y + (ARM_TIP_Y - WAIST_Y) * u,
];

/**
 * Draw the tower into `g`.
 *
 * The base design is in junction-size game units; `sx`/`sy` rescale it for the
 * standard tower (smaller) and the river-crossing giant, which also gets
 * hazard-banded legs and beacons.
 */
export function drawCatFaceTower(g: SVGGElement, o: Tower500Options): void {
  const X = (x: number) => +(x * o.sx).toFixed(2);
  const Y = (y: number) => +((y - 34) * o.sy + o.ground).toFixed(2);

  const path = (pts: number[][], w: number, col?: string) =>
    styled(
      g,
      'path',
      { d: pts.map((p, i) => (i ? 'L' : 'M') + X(p[0]) + ' ' + Y(p[1])).join(' ') },
      `stroke:${col || o.steel};stroke-width:${w};fill:none;stroke-linecap:round;stroke-linejoin:round`,
    );
  const dot = (x: number, y: number, r: number) =>
    styled(g, 'circle', { cx: X(x), cy: Y(y), r }, `fill:${o.dot}`);

  // Hazard paint, night-muted: brick red and pale grey, not signal colors.
  const RED = '#9e4038';
  const WHT = '#b9c2d4';

  for (const s of [1, -1]) {
    const m = (a: number[][]) => a.map(([x, y]) => [x * s, y]);

    // ear: a shield-wire peak standing on the head's top chord
    path(
      m([
        [EAR_ROOT_X, HEAD_TOP_Y],
        [EAR_TIP_X, EAR_TIP_Y],
        [HEAD_TOP_X, HEAD_TOP_Y],
      ]),
      2,
    );
    path(
      m([
        [8.6, -43.9],
        [12.9, -45.2],
      ]),
      1,
    );

    // head: top corner out to the cheek, then in to the crossarm root
    path(
      m([
        [HEAD_TOP_X, HEAD_TOP_Y],
        [HEAD_WIDE_X, HEAD_WIDE_Y],
        [HEAD_FOOT_X, HEAD_FOOT_Y],
      ]),
      2.4,
    );

    // wishbone carrying the centre phase down to its insulator
    path(
      m([
        [9.5, HEAD_TOP_Y],
        [0, DOT_C_Y],
      ]),
      2.6,
    );

    // crossarm: a deep triangle, top chord and lower chord meeting at the tip
    path(m([armTop(0), armTop(1)]), 2.4);
    path(m([armLow(0), armLow(1)]), 2);
    // ...trussed rather than solid, so it reads as steel at any size
    path(
      m([
        armTop(0),
        armLow(0.25),
        armTop(0.25),
        armLow(0.5),
        armTop(0.5),
        armLow(0.75),
        armTop(0.75),
        armLow(1),
      ]),
      1.1,
    );

    // outer phase on a V-string slung under the arm
    path(m([armLow(0.4), [DOT_OUT_X, DOT_OUT_Y], armLow(0.64)]), 1.5);

    // waist: the head's foot pinches in before the body widens again
    path(
      m([
        [HEAD_FOOT_X, HEAD_FOOT_Y],
        [WAIST_X, WAIST_Y],
        [SPLIT_X, SPLIT_Y],
      ]),
      2.4,
    );

    // leg — hazard-banded on the river-crossing giant
    if (o.bands) {
      for (let i = 0; i < 4; i++) {
        const y0 = SPLIT_Y + ((GROUND_Y - SPLIT_Y) * i) / 4;
        const y1 = SPLIT_Y + ((GROUND_Y - SPLIT_Y) * (i + 1)) / 4;
        path(
          m([
            [legX(y0), y0],
            [legX(y1), y1],
          ]),
          4.2,
          i % 2 ? WHT : RED,
        );
      }
    } else {
      path(
        m([
          [SPLIT_X, SPLIT_Y],
          [FOOT_X, GROUND_Y],
        ]),
        2.6,
      );
    }

    // footing
    el(
      'rect',
      {
        x: (s * FOOT_X * o.sx - 3.5).toFixed(1),
        y: Y(GROUND_Y - 1),
        width: 7,
        height: 4,
        rx: 1,
        fill: FOOTING,
      },
      g,
    );

    // shield-wire tip: steel dot, or a blinking beacon on the giant
    if (o.beacon) {
      // A real aircraft-warning light — the one red the board is allowed.
      const b = styled(
        g,
        'circle',
        { cx: X(EAR_TIP_X * s), cy: Y(EAR_TIP_Y - 0.5), r: 2.6 },
        'fill:var(--alert)',
      );
      if (o.animate) {
        el(
          'animate',
          {
            attributeName: 'opacity',
            values: '1;.15;1',
            dur: '1.7s',
            repeatCount: 'indefinite',
          },
          b,
        );
      }
    } else {
      dot(EAR_TIP_X * s, EAR_TIP_Y + 0.3, o.dotR * 0.62);
    }
  }

  // the head's top chord, spanning its full width with an ear at each end
  path(
    [
      [-HEAD_TOP_X, HEAD_TOP_Y],
      [HEAD_TOP_X, HEAD_TOP_Y],
    ],
    2.2,
  );

  // body: rungs and X panels between the waist and the leg split
  let prev = WAIST_Y;
  for (const y of [0.7, 7.3, SPLIT_Y]) {
    path(
      [
        [-bodyX(y), y],
        [bodyX(y), y],
      ],
      1.5,
    );
    path(
      [
        [-bodyX(prev), prev],
        [bodyX(y), y],
      ],
      1.3,
    );
    path(
      [
        [bodyX(prev), prev],
        [-bodyX(y), y],
      ],
      1.3,
    );
    prev = y;
  }

  // the same, carried on down between the splayed legs
  prev = SPLIT_Y;
  for (const y of [20.7, 27.3]) {
    path(
      [
        [-legX(y), y],
        [legX(y), y],
      ],
      1.3,
    );
    path(
      [
        [-legX(prev), prev],
        [legX(y), y],
      ],
      1.1,
    );
    path(
      [
        [legX(prev), prev],
        [-legX(y), y],
      ],
      1.1,
    );
    prev = y;
  }

  // the three phase conductors (delta): one on the wishbone, two under the arms
  dot(0, DOT_C_Y, o.dotR);
  dot(DOT_OUT_X, DOT_OUT_Y, o.dotR);
  dot(-DOT_OUT_X, DOT_OUT_Y, o.dotR);
}

/**
 * The everyday suspension tower — the same drawing as the app icon and the
 * wordmark glyph, at board scale: one T crossarm, a shield-wire peak, an
 * X-braced tapering body.
 *
 * Real grids mix tower families by job, and so does the board: suspension
 * towers carry the ordinary spans, the cat-face takes junctions, the banded
 * giant takes the river. The variety is information — a tower's silhouette
 * tells you what it is doing — and the family stays one hand: same strokes,
 * same steel, same amber insulators.
 */
function drawSuspensionTower(g: SVGGElement): void {
  const seg = (d: string, w: number) => styled(g, 'path', { d }, FRAME + 'stroke-width:' + w);
  const dot = (x: number, y: number) => styled(g, 'circle', { cx: x, cy: y, r: 2.7 }, DOT);

  // legs, tapering from the footings to the crossarm
  seg('M-13 34 L-4.5 -20', 2.4);
  seg('M13 34 L4.5 -20', 2.4);

  // crossarm: a shallow truss — the lower chord rises to meet the top chord
  // at the tips, so the two bars read as one deep arm
  seg('M-20 -20 H20', 2.2);
  seg('M-20 -20 L-15 -14.6 H15 L20 -20', 1.6);

  // peak carrying the shield wire
  seg('M-4.5 -20 L0 -27.5 L4.5 -20', 2);

  // rungs and X panels down the body
  const hw = (y: number) => +(4.5 + (8.5 * (y + 20)) / 54).toFixed(2);
  const rungs = [-6, 8, 22];
  for (const y of rungs) seg(`M${-hw(y)} ${y} H${hw(y)}`, 1.4);
  for (let i = 0; i < rungs.length - 1; i++) {
    const [a, b] = [rungs[i], rungs[i + 1]];
    seg(`M${-hw(a)} ${a} L${hw(b)} ${b}`, 1.2);
    seg(`M${hw(a)} ${a} L${-hw(b)} ${b}`, 1.2);
  }

  // footings
  for (const s of [1, -1]) {
    el('rect', { x: s * 13 - 3.5, y: 33, width: 7, height: 4, rx: 1, fill: FOOTING }, g);
  }

  // insulators: one on each arm tip, one on the peak
  dot(-17.5, -21.3);
  dot(0, -28.2);
  dot(17.5, -21.3);
}

/**
 * Mountains drawn in the same hand as everything else on the board.
 *
 * Topo contour rings were tried and cut: the board's language is pictorial —
 * towers, houses and the plant are all little drawings of the things
 * themselves — so a lone cartographic convention read as noise, and it asked
 * for map literacy the game has no right to assume. A jagged peak silhouette
 * is legible to everyone.
 *
 * Each variant is a ridge outline over the highland fill (blocked terrain is
 * *toned*, exactly as water is), with facet strokes running down the faces
 * and a muted pale cap where the summit catches the light.
 */
const MTN_VARIANTS: { ridge: string; facets: string[]; caps: string[] }[] = [
  {
    // twin peaks, the main one right of centre
    ridge: 'M-46 30 L-18 -14 L-6 -1 L12 -31 L46 30 Z',
    facets: ['M12 -31 L4 -4', 'M-18 -14 L-23 8', 'M24 -12 L31 6'],
    caps: ['M5 -19 L12 -31 L19 -19 L14 -15 L9 -17 Z', 'M-22 -8 L-18 -14 L-14 -8 L-18 -5 Z'],
  },
  {
    // one dominant peak, a foothill on its east shoulder
    ridge: 'M-50 30 L-14 -33 L0 -10 L16 -20 L50 30 Z',
    facets: ['M-14 -33 L-19 -2', 'M16 -20 L11 4'],
    caps: ['M-21 -21 L-14 -33 L-7 -21 L-13 -17 L-18 -19 Z', 'M12 -13 L16 -20 L20 -13 L16 -11 Z'],
  },
  {
    // a three-peak ridge
    ridge: 'M-48 30 L-26 -10 L-14 -1 L2 -28 L18 -6 L30 -13 L48 30 Z',
    facets: ['M2 -28 L-3 -2', 'M-26 -10 L-30 8', 'M30 -13 L26 6'],
    caps: ['M-4 -18 L2 -28 L8 -18 L2 -14 Z', 'M26 -7 L30 -13 L34 -7 L30 -5 Z'],
  },
];

/** Two or three short curved ripple strokes per water tile — not tildes. */
const WATER_VARIANTS: { d: string; opacity?: number }[][] = [
  [
    { d: 'M-34 -16 q8 -7 16 0 t16 0' },
    { d: 'M-20 12 q8 -7 16 0 t16 0', opacity: 0.65 },
    { d: 'M-42 32 q7 -6 14 0', opacity: 0.5 },
  ],
  [
    // the first stroke crosses the tile edge on purpose — water doesn't tile
    { d: 'M-52 -8 q8 -7 16 0 t16 0' },
    { d: 'M-8 18 q8 -7 16 0 t16 0', opacity: 0.6 },
  ],
  [
    { d: 'M-30 -30 q7 -6 14 0 t14 0', opacity: 0.7 },
    { d: 'M-38 6 q8 -7 16 0 t16 0' },
    { d: 'M-6 34 q7 -6 14 0', opacity: 0.5 },
  ],
];

/**
 * Build the sprite library once per board.
 *
 * The tower family, one hand, three jobs: the suspension tower for ordinary
 * spans, the cat-face for junctions, the hazard-banded giant for river
 * crossings. Silhouette = role, at a glance.
 */
export function buildDefs(board: SVGSVGElement, animate: boolean): void {
  const defs = el('defs', {}, board);

  // --- standard: the suspension tower, the wordmark's own drawing (attach ±17.5,−21.3 / 0,−28.2) ---
  const py = el('g', { id: 'pylonShape' }, defs);
  drawSuspensionTower(py);

  // --- junction: full size for towers carrying three or more lines (attach −31) ---
  const jn = el('g', { id: 'junctionShape' }, defs);
  drawCatFaceTower(jn, {
    sx: 1,
    sy: 1,
    ground: 34,
    steel: 'var(--frame)',
    dot: 'var(--dot)',
    dotR: 3.2,
  });

  // --- river-crossing giant: taller, hazard bands + twin beacons (attach −58) ---
  const cr = el('g', { id: 'crossingShape' }, defs);
  drawCatFaceTower(cr, {
    sx: 1.08,
    sy: 1.18,
    ground: 34,
    steel: 'var(--frame)',
    dot: 'var(--dot)',
    dotR: 3.4,
    bands: true,
    beacon: true,
    animate,
  });

  // --- power plant: a steel-drawn machine hall with the one-line-diagram
  //     generator symbol on its face, and a small steady indicator lamp.
  //     A machine that's on, not a bonfire. ---
  const pl = el('g', { id: 'plantShape' }, defs);
  el(
    'rect',
    {
      x: -26,
      y: -4,
      width: 52,
      height: 30,
      rx: 2,
      fill: BODY,
      stroke: '#46536b',
      'stroke-width': 2,
    },
    pl,
  );
  el(
    'rect',
    {
      x: -20,
      y: -20,
      width: 9,
      height: 18,
      rx: 1,
      fill: BODY_HI,
      stroke: '#46536b',
      'stroke-width': 1.5,
    },
    pl,
  );
  el(
    'rect',
    {
      x: -7,
      y: -14,
      width: 9,
      height: 12,
      rx: 1,
      fill: BODY_HI,
      stroke: '#46536b',
      'stroke-width': 1.5,
    },
    pl,
  );
  // the service mast the grid actually hangs from
  styled(pl, 'path', { d: 'M12 -34 L12 -4' }, FRAME + 'stroke-width:2.5');
  styled(pl, 'path', { d: 'M6 -28 L18 -28' }, FRAME + 'stroke-width:2');
  styled(pl, 'circle', { cx: 12, cy: -34, r: 3 }, DOT);
  // generator: a circle with a sine wave — the literal schematic symbol
  styled(pl, 'circle', { cx: -6, cy: 11, r: 8 }, FRAME + 'stroke-width:2');
  styled(pl, 'path', { d: 'M-12 11 q3 -7 6 0 t6 0' }, FRAME + 'stroke-width:1.6');
  // the indicator lamp: on, steady, small
  styled(pl, 'circle', { cx: 19, cy: 3, r: 2 }, DOT);

  // --- house: a dark silhouette in a thin steel line; windows carry the state ---
  const ho = el('g', { id: 'houseShape' }, defs);
  styled(
    ho,
    'rect',
    { x: 10, y: -16, width: 7, height: 12, rx: 1 },
    'fill:var(--roof);stroke:var(--frame);stroke-width:1.2',
  );
  styled(ho, 'path', { d: 'M0 -20 L0 -30' }, 'stroke:var(--frame);stroke-width:2;fill:none');
  styled(ho, 'circle', { cx: 0, cy: -30, r: 2.6 }, 'fill:var(--win)');
  styled(
    ho,
    'path',
    { d: 'M-25 0 L0 -21 L25 0 Z' },
    'fill:var(--roof);stroke:var(--frame);stroke-width:1.5;stroke-linejoin:round',
  );
  styled(
    ho,
    'rect',
    { x: -19, y: 0, width: 38, height: 24, rx: 1.5 },
    'fill:var(--body);stroke:var(--frame);stroke-width:1.5',
  );
  styled(ho, 'rect', { x: -13, y: 6, width: 10, height: 11, rx: 1 }, 'fill:var(--win)');
  styled(ho, 'rect', { x: 4, y: 6, width: 10, height: 11, rx: 1 }, 'fill:var(--win)');

  // --- the windows alone, as an overlay layer: the only glow on the board
  //     belongs to lit windows, so the glow rides a sprite that *is* only
  //     windows (a filter on the full house would halo the whole silhouette) ---
  const hg = el('g', { id: 'houseGlowShape' }, defs);
  styled(hg, 'circle', { cx: 0, cy: -30, r: 2.6 }, 'fill:var(--win)');
  styled(hg, 'rect', { x: -13, y: 6, width: 10, height: 11, rx: 1 }, 'fill:var(--win)');
  styled(hg, 'rect', { x: 4, y: 6, width: 10, height: 11, rx: 1 }, 'fill:var(--win)');

  // --- mountains: concentric contour rings, as elevation appears on a topo map ---
  MTN_VARIANTS.forEach((v, i) => {
    const mt = el('g', { id: 'mtnShape' + i }, defs);
    styled(
      mt,
      'path',
      { d: v.ridge },
      'fill:var(--highland);stroke:var(--contour);stroke-width:2;stroke-linejoin:round',
    );
    for (const f of v.facets) {
      styled(
        mt,
        'path',
        { d: f },
        'stroke:var(--contour);stroke-width:1.4;fill:none;stroke-linecap:round;opacity:.8',
      );
    }
    // moonlit caps — the palest matte the night allows, same pale as the
    // crossing giant's hazard bands
    for (const c of v.caps) styled(mt, 'path', { d: c }, 'fill:#b9c2d4;opacity:.5');
  });

  // --- water: flat night water, darker than land, with a few quiet ripples
  //     on one extremely slow drift ---
  WATER_VARIANTS.forEach((strokes, i) => {
    const wa = el('g', { id: 'waterShape' + i }, defs);
    styled(wa, 'rect', { x: -50, y: -50, width: 100, height: 100 }, 'fill:var(--water)');
    const rip = el('g', {}, wa);
    for (const s of strokes) {
      styled(
        rip,
        'path',
        { d: s.d, ...(s.opacity !== undefined ? { opacity: s.opacity } : {}) },
        'stroke:var(--ripple);stroke-width:2;fill:none;stroke-linecap:round',
      );
    }
    if (animate) {
      el(
        'animateTransform',
        {
          attributeName: 'transform',
          type: 'translate',
          values: '0 0; 3 0; 0 0',
          dur: `${24 + i * 3}s`,
          repeatCount: 'indefinite',
        },
        rip,
      );
    }
  });
}
